import React from 'react';
import moment from 'moment';
import { readDate } from '../../utils/appDate';
import { T, NUMERIC, money } from '../customer/customers.theme';
import {
  CalendarIcon,
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';
import { COPY, vendorBalanceColor, vendorBalanceLabel } from './vendors.copy';

/**
 * Bixin and Deen Cusub, pointed at a supplier.
 *
 * The customers dialog with the sentence reversed: Bixin is the shop paying a
 * supplier down (`credit`), Deen Cusub is a new bill arriving (`debit`). The
 * arithmetic on the server is identical — only who owes whom has flipped.
 *
 * The three fields are the phone's, in the phone's order and with the phone's
 * placeholders: Geli Lacagta, Geli Faahfaahinta, then the date.
 *
 * The same dialog corrects a line already in the book, and as on the customer
 * side it will NOT let you change which way a line points. Turning a bill into
 * a payment is a different entry, not a correction, and doing it by accident
 * moves the balance by twice the amount.
 */
export default function VendorTransactionDialog({
  kind,
  vendor,
  currency,
  transaction,
  onClose,
  onSubmit,
}) {
  const editing = Boolean(transaction);
  const isPayment = kind === 'payment';

  const [amount, setAmount] = React.useState(() => {
    if (!transaction) return '';
    const value = Number(transaction.debit || 0) > 0 ? transaction.debit : transaction.credit;
    return String(value ?? '');
  });
  const [description, setDescription] = React.useState(
    () => (transaction && transaction.description) || ''
  );
  const [date, setDate] = React.useState(() =>
    transaction
      ? readDate(transaction.date).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD')
  );
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const value = Number(amount);
  const valid = Boolean(amount.trim()) && !Number.isNaN(value) && value > 0;

  /** What the balance becomes, shown before it happens. */
  const projected = valid
    ? Number(vendor?.balance || 0) + (isPayment ? -value : value)
    : Number(vendor?.balance || 0);

  const submit = async () => {
    if (busy) return;

    if (!valid) {
      setError(COPY.amountInvalid);
      return;
    }

    setBusy(true);
    setError('');

    try {
      await onSubmit({
        kind: isPayment ? 'payment' : 'charge',
        amount: value,
        // Blank is allowed and falls back to the card's own name, so a hurried
        // entry still says what it was.
        description: description.trim() || (isPayment ? COPY.payment : COPY.charge),
        date,
        transaction,
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.saveFailed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={
            editing
              ? 'Edit Transaction'
              : isPayment
              ? COPY.paymentTitle(vendor?.name || '')
              : COPY.chargeTitle(vendor?.name || '')
          }
          onClose={onClose}
          locked={busy}
        />

        {/* Where the balance stands, and where this line puts it. The second
            row appears only once there is a figure to project. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            margin: '16px 0 4px',
            padding: '12px 14px',
            background: T.sunk,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusControl,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 12.5, color: T.inkSoft }}>{COPY.currentlyOwing}</span>
            <span
              style={{
                fontSize: 13.5,
                fontWeight: 700,
                color: vendorBalanceColor(vendor?.balance),
                ...NUMERIC,
              }}
            >
              {money(vendor?.balance, currency)}
            </span>
          </div>

          {valid ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 12.5, color: T.inkSoft }}>{COPY.afterThis}</span>
              <span
                style={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: vendorBalanceColor(projected),
                  ...NUMERIC,
                }}
              >
                {money(projected, currency)}{' '}
                <span style={{ fontSize: 11, fontWeight: 600, color: T.inkFaint }}>
                  {vendorBalanceLabel(projected)}
                </span>
              </span>
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 14 }}>
          <PillField
            name="vendor-transaction-amount"
            type="number"
            value={amount}
            onChange={(next) => {
              setAmount(next);
              setError('');
            }}
            onEnter={submit}
            placeholder="Geli Lacagta"
            disabled={busy}
            invalid={Boolean(error)}
            autoFocus
          />

          <PillField
            name="vendor-transaction-description"
            value={description}
            onChange={setDescription}
            onEnter={submit}
            placeholder="Geli Faahfaahinta"
            disabled={busy}
          />

          {/* A real date input: the phone opens a picker, and a desktop
              already has one built into the browser. */}
          <PillField
            name="vendor-transaction-date"
            type="date"
            value={date}
            onChange={setDate}
            disabled={busy}
            trailing={
              <span style={{ display: 'flex', color: T.inkFaint }}>
                <CalendarIcon />
              </span>
            }
          />
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 20 }}
        >
          {editing ? 'Update Now' : 'Xarey'}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
